import { Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import ApplicationQuestionsFormSection from 'components/applications/ApplicationQuestionsFormSection';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import routes from 'store/configs/Routes';
import { ApplicationContentView } from 'store/types/ApplicationContent';
import ApplicationPageLayout from 'components/applications/ApplicationPageLayout';
import ApplicationDecisionModal from 'components/applications/ApplicationDecisionModal';
import { UserContext } from 'components/UserGuard';
import { ParametersContext } from 'components/ParametersGuard';
import { getApplicationStatusAlert, getApplicationTitle } from 'util/Application';

import commonStyles from 'styles/common.module.scss';
import styles from './ViewApplicationForm.module.scss';
import pageStyles from '../ApplicationPage.module.scss';

interface ViewApplicationFormProps {
  data: ApplicationContentView;
}

const ViewApplicationForm: React.FunctionComponent<ViewApplicationFormProps> = ({ data }) => {
  const history = useHistory();
  const { id = '' } = useContext(UserContext);
  const {
    claims: { claimsApplicationTypeId, disputesApplicationTypeId },
  } = useContext(ParametersContext);
  const [decisionModalOpen, setDecisionModalOpen] = useState<boolean>(false);
  const [isPrintActive, setIsPrintActive] = useState<boolean>(false);

  const hasContent: boolean = useMemo(() => !!(data && data.sections && data.sections.length), [data]);

  const applicationsUrl = useMemo(
    () =>
      (claimsApplicationTypeId && data.typeId === claimsApplicationTypeId) ||
      (disputesApplicationTypeId && data.typeId === disputesApplicationTypeId)
        ? routes.claims
        : data.createdById && data.applicantId !== data.createdById
        ? routes.companyApplications
        : routes.applications,
    [claimsApplicationTypeId, data.applicantId, data.createdById, data.typeId, disputesApplicationTypeId]
  );

  const handleCancel = useCallback(() => {
    history.push(applicationsUrl);
  }, [applicationsUrl, history]);

  const handlePrintButtonClick = useCallback(() => {
    setTimeout(() => {
      setIsPrintActive(true);
      window.print();
      setIsPrintActive(false);
    }, 0);
  }, []);

  const handleDecisionModalOpen = useCallback(() => {
    setDecisionModalOpen(true);
  }, []);

  const handleDecisionModalClose = useCallback(() => {
    setDecisionModalOpen(false);
  }, []);

  return (
    <>
      <ApplicationPageLayout
        data={data}
        isPrintActive={isPrintActive}
        footerProps={{
          hideSubmitButton: true,
          children: (
            <>
              {id && (
                <Button
                  color={'secondary'}
                  variant={'outlined'}
                  onClick={handleCancel}
                  className={pageStyles.cancelButton}
                >
                  {'Go Back'}
                </Button>
              )}
              {data.decisionDate && (
                <Button color={'secondary'} variant={'outlined'} onClick={handleDecisionModalOpen}>
                  {'View Decision'}
                </Button>
              )}
              {!id && (
                <Button
                  color={'secondary'}
                  variant={'contained'}
                  onClick={handlePrintButtonClick}
                  className={styles.printButton}
                >
                  {'Print'}
                </Button>
              )}
            </>
          ),
        }}
      >
        {data && (
          <>
            <div className={pageStyles.statusAlert}>{getApplicationStatusAlert(data)}</div>
            <div className={pageStyles.questionsWrapper}>
              {data.applicantName && data.title && (
                <h1 className={commonStyles.pageTitle}>{getApplicationTitle(data)}</h1>
              )}
              {hasContent ? (
                <ApplicationQuestionsFormSection sections={data.sections} disabled={true} />
              ) : (
                <Alert severity={'error'} className={commonStyles.alert}>
                  No content found
                </Alert>
              )}
            </div>
          </>
        )}
      </ApplicationPageLayout>
      {data.decisionDate && (
        <ApplicationDecisionModal onClose={handleDecisionModalClose} open={decisionModalOpen} application={data} />
      )}
    </>
  );
};
export default ViewApplicationForm;
